import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import _ from "lodash";
import { Drawer } from "@mui/material";
import ProfileHeader from "../../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { routes } from "../../../../../constants/routes";
import { useLocation, useNavigate } from "react-router-dom";
import ProviderRequestSearchAndFilters from "../../provider-management/provider-requests/search-and-filters";
import "../../provider-management/provider-requests/provider-requests-list.scss";
import ListingTable from "../../../../../common/components/listing-table/list-table";
import { getProviderRequests } from "../../../../../api/admin/provider-management";
import { headers } from "./planner-requests-helper";
import dayjs from "dayjs";
import RequestStatus from "../../../../../common/components/request-status/request-status";
import AdminPaginationComponent from "../../../../../common/components/admin-pagination-component/admin-pagination-component";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import { AlertComponent } from "../../../../../common/components";
import { plannerRequestFilterOptions } from "./planner-requests-helper";
import ProviderRequestsFilterOptions from "../../provider-management/provider-requests/provider-requests-filter-options/provider-requests-filter-options";
import "../../provider-management/provider-requests/provider-requests-filter-options/provider-requests-filter-options.scss";
import { BUSINESS_TYPE_PLANNER } from "../../../../../utils/constants";

const PlannerRequestsList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const breadcrumbLink = [
    {
      label: "Planner Management",
      link: routes.PLANNER_MANAGEMENT,
      handleClick: () => { },
      disablePointerEvents: true,
    },
    { label: "Registration Requests", link: "#", handleClick: () => { } },
  ];

  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<any>(
    location?.state?.searchText || ""
  );
  const [plannerRequestsFilterOptions, setPlannerRequestsFilterOptions] =
    useState<any[]>(plannerRequestFilterOptions);
  const [activeFilters, setActiveFilters] = useState<any>(
    location?.state?.activeFilters || {}
  );
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const isSearchTriggered = useRef(false);
  const pageSize = 10;

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1);
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const fetchPlannerRequests = async (
    page: number,
    search: string,
    filters: any
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params: any = {
        page,
        limit: pageSize,
        offset,
        search: search,
        businessType:
          filters?.businessType?.length ? filters.businessType : BUSINESS_TYPE_PLANNER,
      };
      if (filters?.status) {
        params.status = filters.status;
      }
      if (filters?.createdTimeStart && filters?.createdTimeEnd) {
        params.fromDate = filters.createdTimeStart.startOf("day").toISOString();
        params.toDate = filters.createdTimeEnd.endOf("day").toISOString();
      }
      const response = await getProviderRequests(params);
      if (response?.status === 200 || response?.data?.success) {
        setData(response?.data?.data?.data || []);
        setTotalRecords(response?.data?.data?.totalCount || 0);
      } else {
        setAlertType("error");
        setAlertMessage("Failed to fetch planner requests.");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setAlertType("error");
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch planner requests."
      );
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchPlannerRequests(pageToUse, searchText || "", activeFilters);
    isSearchTriggered.current = false;
  }, [currentPage, searchText, activeFilters]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item: any) => ({
        id: item.id,
        requestId: item.requestId,
        businessName: item.businessName,
        primaryContactName: item.primaryContactName,
        businessType: item.businessType,
        requestedDate: item.requestedDate
          ? dayjs(item.requestedDate).format("MMM D, YYYY")
          : "",
        status: item.status,
      }));
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (
      location.state &&
      location.state.alertMessage &&
      location?.state?.fromView
    ) {
      setAlertMessage(location.state.alertMessage);
      setAlertType(location.state.alertType || "info");
      setAlertOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state && location.state.searchText) {
      setSearchText(location.state.searchText);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (
      location.state &&
      Object.keys(location?.state?.activeFilters || {}).length > 0
    ) {
      setActiveFilters(location.state.activeFilters);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      activeFilters.createdTimeStart || activeFilters.createdTimeEnd;
    const otherFilters = { ...activeFilters };
    delete otherFilters.createdTimeStart;
    delete otherFilters.createdTimeEnd;
    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;
    if (dateFilterActive) count += 1;
    count += otherActiveFilters;
    return count;
  }, [activeFilters]);

  const noResultsTxt = useMemo(() => {
    if (loading) return "Loading planner requests...";
    if (searchText && appliedFilterCount === 0)
      return "No planner requests found for search criteria.";
    if (!searchText && appliedFilterCount > 0)
      return "No planner requests found matching filter criteria.";
    if (searchText && appliedFilterCount > 0)
      return "No planner requests found for search and filter criteria.";
    return "No planner requests found";
  }, [loading, searchText, appliedFilterCount]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="provider-requests-main-wrapper">
      <ProfileHeader breadcrumbLink={breadcrumbLink} />
      <div
        className="provider-requests-filter-and-list-wrapper"
        style={{
          display: "flex",
          flexDirection: "column",
          paddingBottom: totalRecords === 0 ? "10px" : "0px",
        }}
      >
        <div className="provider-requests-search-and-filters">
          <ProviderRequestSearchAndFilters
            onSearch={handleSearch}
            onFilterChange={setActiveFilters}
            appliedFiltersCount={appliedFilterCount}
            isFilterOpen={isOpenFilter}
            setIsFilterOpen={setIsOpenFilter}
            searchRef={searchRef as any}
            localSearchText={searchText}
            isReset={isResetSearch}
            placeholder="Search by Business Name, Request ID"
          />
        </div>
        <div className="provider-requests-listing-table-wrapper-admin">
          <ListingTable
            headers={headers}
            data={formattedData?.map((item) => ({
              ...item,
              status: (
                <RequestStatus
                  status={
                    item.status
                      ? item.status.charAt(0).toUpperCase() +
                      item.status.slice(1)
                      : ""
                  }
                />
              ),
            }))}
            onRowClick={(row) => {
              navigate(
                `${routes.PLANNER_REQUEST_DETAILS.replace(":id", row.id)}`,
                {
                  state: {
                    searchText: searchText,
                    activeFilters: activeFilters,
                  },
                }
              );
            }}
            noResultsText={noResultsTxt}
          />
        </div>
        {totalRecords > 0 && (
          <AdminPaginationComponent
            totalRecords={totalRecords}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
        <Drawer
          anchor="right"
          open={isOpenFilter}
          onClose={() => setIsOpenFilter(false)}
          sx={{
            zIndex: 1300,
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            }
          }}
        >
          <ProviderRequestsFilterOptions
            isOpenFilter={isOpenFilter}
            setIsOpenFilter={setIsOpenFilter}
            providerRequestsFilterOptions={plannerRequestsFilterOptions}
            setProviderRequestsFilterOptions={
              setPlannerRequestsFilterOptions
            }
            activeFilters={activeFilters}
            onApplyFilters={(filters: any) => {
              setActiveFilters(filters);
              setCurrentPage(1);
            }}
          />
        </Drawer>
      </div>
      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage || ""}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default PlannerRequestsList;
